import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useI18n } from '@/hooks/useI18n';
import { usePeriodSelection } from '@/hooks/usePeriodSelection';
import { useOverview, useOverviewSeries } from '@/hooks/queries/useOverview';
import { useIdentities } from '@/hooks/queries/useIdentities';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { analyticsKeys, identityKeys } from '@/lib/queryKeys';
import { PERIOD_DAYS_OPTIONS } from '@/lib/constants';
import { AnalyticsHeader } from './AnalyticsHeader';
import { AnalyticsKpiRow } from './AnalyticsKpiRow';
import { EvolutionSection } from './EvolutionSection';
import { TopAccountsSection } from './TopAccountsSection';
import { IdentityPerformanceSection } from './IdentityPerformanceSection';
import { ViralTimelineSection } from './ViralTimelineSection';
import { LinkReadinessSection } from './LinkReadinessSection';
import { RetirementCandidatesSection } from './RetirementCandidatesSection';
import { EfficiencySection } from './EfficiencySection';
import { AnalyticsDisclosure } from './AnalyticsDisclosure';
import { AnalyticsInsightsSection } from './AnalyticsInsightsSection';
import { GetMySocialSection } from './GetMySocialSection';
import { cn } from '@/lib/cn';
import type { Granularity, TopMetric } from '@/types/enums';

export function AnalyticsScreen() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [periodDays, setPeriodDays] = usePeriodSelection(
    'analytics:periodDays',
    PERIOD_DAYS_OPTIONS,
    30,
  );
  const [selectedGranularity, setGranularity] = useState<Granularity>('DAY');
  const [identityFilter, setIdentityFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const granularity =
    (periodDays === 0 || periodDays > 14) && selectedGranularity === 'HOUR'
      ? 'DAY'
      : selectedGranularity;
  const overview = useOverview(periodDays);
  const series = useOverviewSeries(periodDays, identityFilter === 'all' ? granularity : 'DAY');
  const identities = useIdentities();

  async function refresh() {
    setRefreshing(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: analyticsKeys.all }),
        queryClient.invalidateQueries({ queryKey: identityKeys.all }),
      ]);
    } finally {
      setRefreshing(false);
    }
  }

  function changePeriod(days: number) {
    setPeriodDays(days);
    if (days === 0 || days > 14)
      setGranularity((current) => (current === 'HOUR' ? 'DAY' : current));
  }

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <AnalyticsHeader
        periodDays={periodDays}
        onPeriodChange={changePeriod}
        updatedAt={overview.dataUpdatedAt}
        refreshing={refreshing || overview.isFetching}
        onRefresh={() => {
          void refresh();
        }}
      />
      {overview.error ? (
        <ErrorInline
          error={overview.error}
          onRetry={() => {
            void overview.refetch();
          }}
        />
      ) : (
        <AnalyticsKpiRow
          overview={overview.data}
          periodDays={periodDays}
          loading={overview.isLoading}
        />
      )}

      {identities.error && (
        <ErrorInline
          error={identities.error}
          onRetry={() => {
            void identities.refetch();
          }}
        />
      )}
      <EvolutionSection
        periodDays={periodDays}
        granularity={granularity}
        onGranularityChange={setGranularity}
        identityId={identityFilter === 'all' ? undefined : identityFilter}
        onIdentityChange={setIdentityFilter}
        identities={identities.data}
        referenceTime={overview.dataUpdatedAt || undefined}
        series={series.data}
        loading={series.isLoading}
        error={series.error}
        onRetry={() => {
          void series.refetch();
        }}
      />

      <GetMySocialSection
        periodDays={periodDays}
        identityId={identityFilter === 'all' ? undefined : identityFilter}
        identityName={identities.data?.find((identity) => identity.id === identityFilter)?.name}
      />
      <AnalyticsInsightsSection periodDays={periodDays} />

      <section
        id="analytics-details"
        className="flex scroll-mt-24 flex-col gap-3"
        aria-labelledby="analytics-details-title"
      >
        <header className="mb-1 flex flex-col gap-1.5">
          <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-violet-deep">
            {t('Pour aller plus loin', 'Explore further')}
          </p>
          <h2
            id="analytics-details-title"
            className="text-[18px] font-semibold tracking-tight text-text-1"
          >
            {t('Détails et diagnostics', 'Details and diagnostics')}
          </h2>
          <p className="text-[12px] text-text-3">
            {t(
              'Explorez vos classements et les comptes qui méritent votre attention.',
              'Explore your rankings and the accounts that need your attention.',
            )}
          </p>
        </header>
        <AnalyticsDisclosure
          title={t('Classements détaillés', 'Detailed rankings')}
          description={t(
            'Comparez les comptes par vues ou abonnés, et consultez les résultats par identité.',
            'Compare accounts by views or followers, and explore results by identity.',
          )}
          badge={t('Comptes · Identités', 'Accounts · Identities')}
        >
          <DetailedRankings periodDays={periodDays} />
        </AnalyticsDisclosure>
        <AnalyticsDisclosure
          title={t('Reels viraux', 'Viral reels')}
          badge={t('Période sélectionnée', 'Selected period')}
          description={t(
            'Quand vos reels ont été détectés comme viraux, et lesquels.',
            'When your reels were detected as viral, and which ones.',
          )}
        >
          <ViralAnalysis periodDays={periodDays} />
        </AnalyticsDisclosure>
        <AnalyticsDisclosure
          title={t('Potentiel des liens en story', 'Story link potential')}
          badge={t('14 derniers jours', 'Last 14 days')}
          description={t(
            'Repérez les comptes qui combinent volume de vues et régularité.',
            'Find accounts that combine view volume and consistency.',
          )}
        >
          <LinkReadinessSection />
        </AnalyticsDisclosure>
        <AnalyticsDisclosure
          title={t('Comptes à surveiller', 'Accounts to watch')}
          badge={t('14 derniers jours', 'Last 14 days')}
          description={t(
            'Repérez les comptes suivis depuis plus de 30 jours avec peu de vues récentes.',
            'Find accounts tracked for more than 30 days with few recent views.',
          )}
        >
          <RetirementCandidatesSection />
        </AnalyticsDisclosure>
        <AnalyticsDisclosure
          title={t('Vues rapportées aux publications', 'Views relative to publications')}
          badge={t('Période sélectionnée', 'Selected period')}
          description={t(
            'Comparez les vues gagnées au nombre total de publications connu.',
            'Compare views gained with the latest known total publication count.',
          )}
        >
          <EfficiencySection periodDays={periodDays} />
        </AnalyticsDisclosure>
      </section>
      <details className="rounded-2xl border border-line-1 bg-bg-1 px-4 py-1 text-[12px] text-text-3 sm:px-5">
        <summary className="min-h-11 cursor-pointer py-3 font-medium text-text-2">
          {t('Comment lire ces chiffres ?', 'How to read these numbers')}
        </summary>
        <dl className="space-y-3 pb-4 leading-relaxed">
          <div>
            <dt className="font-medium text-text-2">{t('Vues gagnées', 'Views gained')}</dt>
            <dd>
              {t(
                'Vues supplémentaires observées sur les reels suivis. Le premier relevé sert de référence, sauf si le reel a été publié pendant la période : ses premières vues sont alors incluses. Une baisse puis un retour au même niveau ne compte pas de nouvelles vues.',
                'Additional views observed on tracked reels. The first reading is the baseline, unless the reel was published within the period: its initial views are then included. A drop followed by a return to the same count does not add new views.',
              )}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-text-2">
              {t('Périodes et journées', 'Periods and days')}
            </dt>
            <dd>
              {t(
                'La période est glissante et se termine au moment du calcul. Les journées et semaines du graphique suivent le fuseau UTC. Le premier intervalle peut être partiel et le dernier est encore en cours.',
                'The period is rolling and ends when calculated. Chart days and weeks follow UTC. The first interval may be partial and the current interval is still in progress.',
              )}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-text-2">{t('Données manquantes', 'Missing data')}</dt>
            <dd>
              {t(
                'Une absence de mesure ne signifie pas zéro vue. Les abonnés sont les derniers totaux connus, pas les abonnés gagnés pendant la période. Actualiser recharge les analyses ; cela ne lance pas de nouvelle collecte Instagram.',
                'A missing measurement does not mean zero views. Followers are the latest known totals, not followers gained during the period. Refreshing reloads the analysis; it does not start a new Instagram collection.',
              )}
            </dd>
          </div>
        </dl>
      </details>
    </div>
  );
}

function DetailedRankings({ periodDays }: { periodDays: number }) {
  const { t } = useI18n();
  const [view, setView] = useState<'accounts' | 'identities'>('accounts');
  const [metric, setMetric] = useState<TopMetric>('VIEWS_GROWTH');

  return (
    <div className="min-w-0 space-y-4">
      <div
        className="inline-flex gap-1 rounded-lg bg-bg-2 p-1"
        aria-label={t('Type de classement', 'Ranking type')}
      >
        {(['accounts', 'identities'] as const).map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={view === value}
            onClick={() => setView(value)}
            className={cn(
              'min-h-11 rounded-md px-4 text-[12px] font-medium transition-colors sm:min-h-8',
              view === value ? 'bg-bg-1 text-text-1 shadow-card' : 'text-text-3 hover:text-text-1',
            )}
          >
            {value === 'accounts' ? t('Comptes', 'Accounts') : t('Identités', 'Identities')}
          </button>
        ))}
      </div>
      {view === 'accounts' ? (
        <div id="analytics-accounts">
          <TopAccountsSection periodDays={periodDays} metric={metric} onMetricChange={setMetric} />
        </div>
      ) : (
        <div id="analytics-identities">
          <IdentityPerformanceSection periodDays={periodDays} />
        </div>
      )}
    </div>
  );
}

function ViralAnalysis({ periodDays }: { periodDays: number }) {
  const series = useOverviewSeries(periodDays, 'DAY');
  return (
    <ViralTimelineSection
      periodDays={periodDays}
      viralSeries={series.data?.viralReels}
      isLoading={series.isLoading}
      error={series.error}
      onRetry={() => {
        void series.refetch();
      }}
    />
  );
}
